/**
 * Site Config Composable
 * 管理網站配置
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/siteConfig.js)
 * - API：使用 useFetch 支援 SSR，透過 onResponse 同步處理資料
 * - 單例模式：使用 nuxtApp._siteContext 避免 storeToRefs 重複創建
 */

import { decode } from 'js-base64'
import { storeToRefs } from 'pinia'
import { getCurrentInstance, onMounted } from 'vue'

import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { storage } from '@/utils/storage'
import { commonApi } from '~/api/common'
import { useSiteConfigStore } from '~/stores/siteConfig'

export const useSite = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useFetch 和 storeToRefs 只創建一次
  if (!nuxtApp._siteContext) {
    const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public
    const store = useSiteConfigStore()

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 從 commonApi 取得 API 配置
    const apiConfig = commonApi.getConfig({ appSecret })
    const apiPath = apiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    const { refresh, data: fetchData } = useFetch(`${baseUrl}/${apiPath}`, {
      key: 'site-config',
      dedupe: 'defer',
      query: apiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      onResponse({ response }) {
        if (response._data?.config) {
          const config = JSON.parse(decode(response._data.config))
          store.setSiteConfig(config)
        }
      },
      onResponseError({ error: err }) {
        console.error('[useSite] fetchSiteConfig API error:', err)
        store.setSiteConfig({})
      },
    })

    // getCachedData 命中快取時 onResponse 不會執行，手動同步快取資料到 store
    // siteConfig 初始值為 {}（truthy），需用 Object.keys 判斷是否為空物件
    if (fetchData.value?.config && Object.keys(store.siteConfig || {}).length === 0) {
      const config = JSON.parse(decode(fetchData.value.config))
      store.setSiteConfig(config)
    }

    // storeToRefs 只調用一次
    nuxtApp._siteContext = {
      ...storeToRefs(store),
      fetchSiteConfig: refresh,
    }
  }

  // Client 端 mounted 後存儲 siteConfig 到 localStorage
  // 只有在組件上下文中才註冊 onMounted，避免在 plugin/middleware 中調用時出現警告
  if (getCurrentInstance()) {
    const store = useSiteConfigStore()
    onMounted(() => {
      if (store.siteConfig) {
        storage.set('site-config', store.siteConfig)
      }
    })
  }

  return nuxtApp._siteContext
}
